import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";
import { ParamKeyValuePair } from "react-router-dom/dist/dom";

import { CmtPage } from "@app/components/page/CmtPage.tsx";
import { CmtLastErpSync } from "@app/components/settings/CmtLastErpSync.tsx";
import { CmtUsersGrid } from "@app/components/users/CmtUsersGrid.tsx";
import { UserPageFilters } from "@app/components/users/filters/UserPageFilters.tsx";
import {
  DEFAULT_PAGE,
  USERS_PAGE_SIZE,
} from "@app/constants/paginationConstants.ts";
import { DEFAULT_USER_STATUS, UserStatusFilter } from "@app/models/User.ts";
import {
  DEPARTMENTS_PARAM,
  SEARCH_PARAM,
  STATUS_PARAM,
} from "@app/services/api/useUserService.ts";
import { useUsers } from "@app/services/hook/useUsers.tsx";
import { showNotification } from "@app/services/notification/showNotification.tsx";

const extractParams = (
  page: number = DEFAULT_PAGE,
  departmentIds: string[] = [],
  search: string | undefined = undefined,
  status: UserStatusFilter = DEFAULT_USER_STATUS,
): ParamKeyValuePair[] => [
  ["page", String(page)],
  [SEARCH_PARAM, search ?? ""],
  [STATUS_PARAM, status],
  ...departmentIds.map(
    (departmentId): ParamKeyValuePair => [DEPARTMENTS_PARAM, departmentId],
  ),
];

const USER_STATUSES: UserStatusFilter[] = ["active", "bench", "all"];

const parseStatus = (value: string | null): UserStatusFilter =>
  USER_STATUSES.find((status) => status === value) ?? DEFAULT_USER_STATUS;

export const UsersPage = () => {
  const intl = useIntl();

  const [searchParams, setSearchParams] = useSearchParams();

  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get("page") ?? DEFAULT_PAGE),
  );
  const [searchUser, setSearchUser] = useState<string | undefined>(
    searchParams.get(SEARCH_PARAM) ?? undefined,
  );
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>(
    searchParams.getAll(DEPARTMENTS_PARAM),
  );
  const [status, setStatus] = useState<UserStatusFilter>(
    parseStatus(searchParams.get(STATUS_PARAM)),
  );

  const { users, isLoading, error } = useUsers(
    currentPage,
    USERS_PAGE_SIZE,
    searchUser,
    selectedDepartmentIds,
    status,
  );

  useEffect(() => {
    if (error) {
      showNotification({
        type: "error",
        title: intl.formatMessage({
          id: "error.title",
          defaultMessage: "Error",
        }),
        description: intl.formatMessage({
          id: "error.users.description",
          defaultMessage: "Failed to access users",
        }),
      });
    }
  }, [error, intl]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    setSearchParams(
      extractParams(newPage, selectedDepartmentIds, searchUser, status),
    );
  };

  const handleSearchChange = (value: string) => {
    setSearchUser(value);
    setCurrentPage(DEFAULT_PAGE);
    setSearchParams(
      extractParams(DEFAULT_PAGE, selectedDepartmentIds, value, status),
    );
  };

  const handleDepartmentChange = (departmentIds: string[]) => {
    setSelectedDepartmentIds(departmentIds);
    setCurrentPage(DEFAULT_PAGE);
    setSearchParams(
      extractParams(DEFAULT_PAGE, departmentIds, searchUser, status),
    );
  };

  const handleStatusChange = (value: UserStatusFilter) => {
    setStatus(value);
    setCurrentPage(DEFAULT_PAGE);
    setSearchParams(
      extractParams(DEFAULT_PAGE, selectedDepartmentIds, searchUser, value),
    );
  };

  const resetFilters = () => {
    setStatus(DEFAULT_USER_STATUS);
    setCurrentPage(DEFAULT_PAGE);
    setSelectedDepartmentIds([]);
    setSearchUser(undefined);
    setSearchParams(extractParams());
  };

  return (
    <CmtPage
      eyebrow={
        <FormattedMessage id="users.eyebrow" defaultMessage="Directory" />
      }
      title={<FormattedMessage id="app.title" defaultMessage="Employees" />}
      extra={<CmtLastErpSync />}
    >
      <UserPageFilters
        status={status}
        search={searchUser}
        departmentIds={selectedDepartmentIds}
        handleSearchChange={handleSearchChange}
        handleDepartmentChange={handleDepartmentChange}
        handleStatusChange={handleStatusChange}
        reset={resetFilters}
      />
      <CmtUsersGrid
        users={users}
        isLoading={isLoading}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />
    </CmtPage>
  );
};
